import React from 'react';
import PropTypes from 'prop-types';
import StudentRepository from '../services/StudentRepository.js';
import {
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Button,
  CircularProgress,
  Alert,
  Typography,
  FormLabel,
} from '@mui/material';
import FormButtonGroup from '../components/layout/FormButtonGroup.jsx';

export default function Address({
  repo,
  handleScreenNext,
  handleScreenBack,
  handleErrors,
  formValues,
  setFormValues,
  onSkip,
}) {
  const [loading, setLoading] = React.useState(false);
  const [sameAsPermanentAddress, setSameAsPermanentAddress] = React.useState(false);
  const handleValueChange = (values) => {
    values = {
      ...formValues,
      ...values,
    };
    if (sameAsPermanentAddress)
      values = {
        ...values,
        ca_unit: values.pa_unit,
        ca_street: values.pa_street,
        ca_brgy: values.pa_brgy,
        ca_city: values.pa_city,
        ca_province: values.pa_province,
        ca_country: values.pa_country,
      };
    setFormValues(values);
  };
  const handleInputChange = (evt) => {
    const name = evt.target.name;
    const value = evt.target.value;
    handleValueChange({
      [name]: value,
    });
  };
  const handleCheckboxChange = (event) => {
    setSameAsPermanentAddress(event.target.checked);
    handleValueChange({
      ca_unit: formValues.pa_unit,
      ca_street: formValues.pa_street,
      ca_brgy: formValues.pa_brgy,
      ca_city: formValues.pa_city,
      ca_province: formValues.pa_province,
      ca_country: formValues.pa_country,
    });
  };
  const isShiftable = () => {
    return (
      formValues.pa_unit &&
      formValues.pa_street &&
      formValues.pa_brgy &&
      formValues.pa_city &&
      formValues.pa_province &&
      formValues.pa_country &&
      formValues.ca_unit &&
      formValues.ca_street &&
      formValues.ca_brgy &&
      formValues.ca_city &&
      formValues.ca_province &&
      formValues.ca_country &&
      !loading
    );
  };
  const handleNext = async () => {
    setLoading(true);
    repo
      .updateAddresses({
        ca_unit: formValues.ca_unit,
        ca_street: formValues.ca_street,
        ca_brgy: formValues.ca_brgy,
        ca_city: formValues.ca_city,
        ca_province: formValues.ca_province,
        ca_country: formValues.ca_country,
        pa_unit: formValues.pa_unit,
        pa_street: formValues.pa_street,
        pa_brgy: formValues.pa_brgy,
        pa_city: formValues.pa_city,
        pa_province: formValues.pa_province,
        pa_country: formValues.pa_country,
      })
      .then(() => {
        setLoading(false);
        handleScreenNext();
      })
      .catch(() => {
        setLoading(false);
        handleErrors(
          <Alert variant="filled" severity="error" sx={{ marginBottom: 3 }}>
            <Typography variant="caption">
              We are unable to reflect the changes in your addresses. Please contact the
              administrator at
              <a href="mailto:webteam@msuiit.edu.ph">webteam@msuiit.edu.ph</a>.
            </Typography>
          </Alert>
        );
      });
  };
  return (
    <Box
      sx={{
        minHeight: '-webkit-min-content',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '30px',
      }}
    >
      <Box>
        <FormLabel>Home/Permanent Address</FormLabel>
        <TextField
          required
          id="pa_unit"
          name="pa_unit"
          label="Lot/Block/House No."
          variant="outlined"
          onChange={handleInputChange}
          value={formValues.pa_unit ? formValues.pa_unit : ''}
          fullWidth
          margin="dense"
        />
        <TextField
          required
          id="pa_street"
          name="pa_street"
          label="Street Address"
          variant="outlined"
          onChange={handleInputChange}
          value={formValues.pa_street ? formValues.pa_street : ''}
          multiline
          fullWidth
          margin="dense"
        />
        <TextField
          required
          id="pa_brgy"
          name="pa_brgy"
          label="Barangay"
          variant="outlined"
          onChange={handleInputChange}
          value={formValues.pa_brgy ? formValues.pa_brgy : ''}
          multiline
          fullWidth
          margin="dense"
        />
        <TextField
          required
          id="pa_city"
          name="pa_city"
          label="City"
          variant="outlined"
          onChange={handleInputChange}
          value={formValues.pa_city ? formValues.pa_city : ''}
          multiline
          fullWidth
          margin="dense"
        />
        <TextField
          required
          id="pa_province"
          name="pa_province"
          label="Province"
          variant="outlined"
          onChange={handleInputChange}
          value={formValues.pa_province ? formValues.pa_province : ''}
          multiline
          fullWidth
          margin="dense"
        />
        <TextField
          required
          id="pa_country"
          name="pa_country"
          label="Country"
          variant="outlined"
          onChange={handleInputChange}
          value={formValues.pa_country ? formValues.pa_country : ''}
          multiline
          fullWidth
          margin="dense"
        />
      </Box>
      <Box sx={{ flexDirection: 'row' }}>
        <FormLabel>Current Address</FormLabel>
        <FormControlLabel
          control={<Checkbox checked={sameAsPermanentAddress} onChange={handleCheckboxChange} />}
          label="Same as permanent address"
          size="small"
        />
        <TextField
          required
          id="ca_unit"
          name="ca_unit"
          label="Lot/Block/House No."
          variant="outlined"
          onChange={handleInputChange}
          value={formValues.ca_unit ? formValues.ca_unit : ''}
          fullWidth
          margin="dense"
        />
        <TextField
          required
          id="ca_street"
          name="ca_street"
          label="Street Address"
          variant="outlined"
          onChange={handleInputChange}
          value={formValues.ca_street ? formValues.ca_street : ''}
          multiline
          fullWidth
          margin="dense"
        />
        <TextField
          required
          id="ca_brgy"
          name="ca_brgy"
          label="Barangay"
          variant="outlined"
          onChange={handleInputChange}
          value={formValues.ca_brgy ? formValues.ca_brgy : ''}
          multiline
          fullWidth
          margin="dense"
        />
        <TextField
          required
          id="ca_city"
          name="ca_city"
          label="City"
          variant="outlined"
          onChange={handleInputChange}
          value={formValues.ca_city ? formValues.ca_city : ''}
          multiline
          fullWidth
          margin="dense"
        />
        <TextField
          required
          id="ca_province"
          name="ca_province"
          label="Province"
          variant="outlined"
          onChange={handleInputChange}
          value={formValues.ca_province ? formValues.ca_province : ''}
          multiline
          fullWidth
          margin="dense"
        />
        <TextField
          required
          id="ca_country"
          name="ca_country"
          label="Country"
          variant="outlined"
          onChange={handleInputChange}
          value={formValues.ca_country ? formValues.ca_country : ''}
          multiline
          fullWidth
          margin="dense"
        />
      </Box>
      <Box sx={{ marginTop: '20px' }}>
        <FormButtonGroup>
          <Button variant="contained" size="large" color="error" onClick={() => handleScreenBack()}>
            Back
          </Button>
          {onSkip && (
            <Button
              variant="outlined"
              size="large"
              color="primary"
              onClick={onSkip}
              sx={{ marginRight: '10px' }}
            >
              Skip to Finish
            </Button>
          )}
          <Box sx={{ m: 0, position: 'relative' }}>
            <Button
              variant="contained"
              size="large"
              color="primary"
              disabled={!isShiftable()}
              onClick={handleNext}
            >
              Next
            </Button>
            {loading && (
              <CircularProgress
                size={24}
                color={'success'}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </Box>
        </FormButtonGroup>
      </Box>
    </Box>
  );
}

Address.propTypes = {
  repo: PropTypes.instanceOf(StudentRepository),
  studentId: PropTypes.string,
  handleScreenNext: PropTypes.func,
  handleScreenBack: PropTypes.func,
  handleErrors: PropTypes.func,
  formValues: PropTypes.object,
  setFormValues: PropTypes.func,
  onSkip: PropTypes.func,
};
